import React, { useEffect, useRef } from 'react';
import { Formik, Field } from 'formik';
import { InputField } from '../../../../components/formFields/InputField';
import { SelectFormField } from '../../../../components/formFields/SelectFormField';
import { DatePickerField } from '../../../../components/formFields/DatePickerField';
import * as Yup from 'yup';
import { validDate } from '../../../../utils/time';
import { getFieldErrors } from '../../../../utils/apiError';

const ValidationSchema = Yup.object().shape({
  employee_email: Yup.string().email('Zadejte validní e-mail!').nullable(true),
});

interface UsersFormProps {
  formProps: Record<string, any>;
  submitOutside: boolean;
  submit: (values: any) => void;
  error?: any;
  reset?: boolean;
  resetConfirm?: () => void;
  roles: any[];
  isNewEmployee?: boolean;
}

const UsersForm = ({
  formProps,
  submitOutside,
  submit,
  error,
  reset = false,
  resetConfirm,
  roles,
  isNewEmployee = false,
}: UsersFormProps) => {
  const formRef = useRef<any>();

  useEffect(() => {
    if (reset === true) {
      formRef.current.setValues(init(formProps));
      resetConfirm();
    }
  }, [reset]);

  useEffect(() => {
    let isSubscribed = true;
    if (isSubscribed && submitOutside) {
      formRef.current.submitForm();
    }

    return () => {
      isSubscribed = false;
    };
  }, [submitOutside]);

  useEffect(() => {
    formRef.current.setErrors(getFieldErrors(error));
  }, [error]);

  const init = ({
    role,
    employee_email,
    employee_job_title,
    employee_status,
    employee_employed_start,
    employee_employed_end,
  }: any) => {
    const employeeRole = role?.id;
    return {
      role: employeeRole,
      employee_email,
      employee_job_title,
      employee_status,
      employee_employed_start,
      employee_employed_end,
    };
  };
  return (
    <div>
      <Formik
        validationSchema={ValidationSchema}
        innerRef={formRef}
        enableReinitialize={true}
        initialValues={init(formProps)}
        onSubmit={(values) => {
          submit(values);
        }}
      >
        {(props) => {
          const { values, setFieldValue } = props;

          return (
            <div className="space-y-1">
              <SelectFormField
                name="role"
                data={roles}
                label="Označení"
                placeholder="Role"
                data-testid="users-form-role-select"
              />
              <Field
                name="employee_email"
                value={values.employee_email}
                label="Přiřazený pracovní email"
                placeholder="Přiřazený pracovní email"
                component={InputField}
                data-testid="users-form-email-input"
              />
              <Field
                name="employee_job_title"
                value={values.employee_job_title}
                label="Název pracovní pozice"
                placeholder="Název pracovní pozice"
                component={InputField}
                data-testid="users-form-job-title-input"
              />
              {!isNewEmployee && (
                <SelectFormField
                  name="employee_status"
                  label="Stav"
                  placeholder="Stav"
                  data-testid="users-form-status-select"
                  data={[
                    {
                      id: true as any,
                      title: 'aktivní',
                    },
                    { id: false as any, title: 'neaktivní' },
                  ]}
                />
              )}
              <Field
                name="employee_employed_start"
                value={validDate(values.employee_employed_start)}
                label="Datum nástupu pracovníka"
                placeholder="Datum nástupu pracovníka"
                onChange={(date: any) => setFieldValue('employee_employed_start', date)}
                component={DatePickerField}
                data-testid="users-form-start-date-input"
              />
              {!isNewEmployee && (
                <Field
                  name="employee_employed_end"
                  value={validDate(values.employee_employed_end)}
                  label="Datum odchodu pracovníka"
                  placeholder="Datum odchodu pracovníka"
                  component={DatePickerField}
                  onChange={(date: any) => setFieldValue('employee_employed_end', date)}
                  data-testid="users-form-end-date-input"
                />
              )}
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default UsersForm;
